import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
import type { DeepPartial, Schema } from '~~/common/types/api';

type ContentMenuBase = Schema<'ContentMenuModel-default'>;

//todo update interface: remove Omit logic when API documentation is updated
interface ContentMenu extends Omit<DeepPartial<ContentMenuBase>, 'children'> {
  children?: ContentMenu[];
}

interface TransformedMenuItem {
  label: string | undefined;
  to: string | null | undefined;
  target: '_blank' | null;
}

export const useContentMenusStore = defineStore('contentMenus', () => {
  const nuxtApp = useNuxtApp();
  const footerMenu = ref<ContentMenu | null>(null);
  const petrolMenu = ref<TransformedMenuItem[]>([]);
  const loyaltyProgramMenu = ref<TransformedMenuItem[]>([]);
  const careerMenu = ref<TransformedMenuItem[]>([]);
  const localFlavorsMenu = ref<TransformedMenuItem[]>([]);

  /**
   * Link to the career landing page, falling back to a static path when the
   * career-menu is empty.
  */
  const careerHomeLink = computed(() => careerMenu.value[0]?.to ?? '/karrier');

  /**
   * Link to the petrol (gas station) landing page. Unlike `/karrier` — which the
   * [...slug] catch-all always renders as the job list — the petrol landing is a
   * plain CMS page, so it only exists if the petrol-menu says so. No static
   * fallback: an empty menu means there is no landing page to link to.
  */
  const petrolHomeLink = computed(() => petrolMenu.value[0]?.to);

  async function initMenu (path: string) {
    const { $logger } = nuxtApp;
    try {
      const menu = await fetchContentMenu(path);
      if (!menu) {
        return;
      }

      switch (path) {
        case 'loyalty-program-menu':
          loyaltyProgramMenu.value = menu.children?.map(menuTransformer) ?? [];
          break;
        case 'career-menu':
          careerMenu.value = menu.children?.map(menuTransformer) ?? [];
          break;
        case 'petrol-menu':
          petrolMenu.value = menu.children?.map(menuTransformer) ?? [];
          break;
        case 'footer-menu':
          footerMenu.value = menu;
          break;
        case 'local-flavors-menu':
          localFlavorsMenu.value = menu.children?.map(menuTransformer) ?? [];
          break;
      }
    } catch (err) {
      $logger.error(err);
    }
  }

  async function fetchContentMenu (contentMenuId: string): Promise<ContentMenu | null> {
    const { $api, $logger } = nuxtApp;
    try {
      if (contentMenuId) {
        return await $api.$get<ContentMenu>(`/content_menus/${contentMenuId}`, { awAxCache: AW_AX_CACHE_GLOBAL });
      } else {
        throw new Error(`contentMenuId is ${contentMenuId}`);
      }
    } catch (err) {
      $logger.error(err);
      return null;
    }
  }

  function getFirstLinkFromContentMenu (contentMenu: ContentMenu | null) {
    return contentMenu?.children?.[0]?.link;
  }

  return {
    footerMenu,
    petrolMenu,
    petrolHomeLink,
    careerMenu,
    careerHomeLink,
    loyaltyProgramMenu,
    localFlavorsMenu,
    initMenu,
    fetchContentMenu,
    getFirstLinkFromContentMenu,
  };
});

function menuTransformer (menu: ContentMenu): TransformedMenuItem {
  return {
    label: menu.title,
    to: menu.link,
    target: menu.newTab ? '_blank' : null,
  };
}
